import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Shield,
  Calendar,
  CheckCircle,
  DollarSign,
  Clock,
  Truck,
  Award,
  Headphones,
  CreditCard,
  CalendarDays,
  ShieldCheck,
  MapPin,
  Receipt,
  ShieldAlert,
  Star,
  TrendingUp
} from 'lucide-react';

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(4); // Start with middle card (Real-time Tracking)
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useRef(null);
  const autoAdvanceRef = useRef(null);

  // Fixed features array with proper icon imports and enhanced SEO content
  const features = useMemo(() => [
    {
      icon: Headphones,
      title: "24/7 Support",
      description: "Round-the-clock reliable support – we're here whenever you need assistance",
      ariaLabel: "24/7 customer support available round the clock for assistance"
    },
    {
      icon: CreditCard,
      title: "Online Payment",
      description: "Secure, fast, and hassle-free online payments for easy transactions",
      ariaLabel: "Secure online payment system for easy and safe transactions"
    },
    {
      icon: CalendarDays,
      title: "Quick Booking",
      description: "Book your shipment instantly with RoadExpress – Fast, Reliable, Hassle-Free",
      ariaLabel: "Quick and instant booking system for hassle-free shipment scheduling"
    },
    {
      icon: ShieldCheck,
      title: "Verified Vehicles",
      description: "Move with confidence – our verified vehicles ensure safe and reliable transport",
      ariaLabel: "Verified vehicles ensuring safe and reliable transportation services"
    },
    {
      icon: MapPin,
      title: "Real-time Tracking",
      description: "Track your shipment in real-time with RoadExpress – Stay informed, stay in control",
      ariaLabel: "Real-time shipment tracking system for complete visibility and control"
    },
    {
      icon: Receipt,
      title: "Transparent Pricing",
      description: "No hidden costs – just fair, transparent pricing you can trust",
      ariaLabel: "Transparent pricing with no hidden costs for shipping services"
    },
    {
      icon: ShieldAlert,
      title: "Safe & Secure Goods",
      description: "Your goods, our priority – safely delivered with complete security and care",
      ariaLabel: "Safe and secure goods transportation with complete security guarantee"
    },
    {
      icon: Star,
      title: "Trip Rating",
      description: "Rate your experience and help us maintain the highest service standards",
      ariaLabel: "Trip rating system for continuous service quality improvement"
    },
    {
      icon: TrendingUp,
      title: "Affordable Price",
      description: "Quality service at an affordable price – delivering value without compromise",
      ariaLabel: "Affordable pricing for high-quality logistics services"
    }
  ], []);

  // Cleanup function for intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Auto-advance with proper cleanup
  const startAutoAdvance = useCallback(() => {
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
    }

    autoAdvanceRef.current = setInterval(() => {
      if (!isAnimating && hoveredIndex === null && !isPaused) {
        setIsAnimating(true);
        setActiveIndex((prev) => (prev + 1) % features.length);
        setTimeout(() => setIsAnimating(false), 800);
      }
    }, 1000); // 3 seconds for better UX
  }, [isAnimating, hoveredIndex, isPaused, features.length]);

  const stopAutoAdvance = useCallback(() => {
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
  }, []);

  useEffect(() => {
    startAutoAdvance();
    return () => stopAutoAdvance();
  }, [startAutoAdvance, stopAutoAdvance]);

  // Handle user interactions
  const handleCardClick = useCallback((index) => {
    if (isAnimating || index === activeIndex) return;

    setIsPaused(true);
    setIsAnimating(true);
    setActiveIndex(index);

    setTimeout(() => {
      setIsAnimating(false);
      setTimeout(() => setIsPaused(false), 1000);
    }, 500);
  }, [isAnimating, activeIndex]);

  const handleMouseEnter = useCallback((index) => {
    setHoveredIndex(index);
    setIsPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
    setTimeout(() => setIsPaused(false), 200);
  }, []);

  const getCardPosition = useCallback((index) => {
    const diff = (index - activeIndex + features.length) % features.length;

    if (diff === 0) return "active";
    else if (diff === 1) return "next1";
    else if (diff === 2) return "next2";
    else if (diff === 3) return "next3";
    else if (diff === features.length - 3) return "prev3";
    else if (diff === features.length - 2) return "prev2";
    else if (diff === features.length - 1) return "prev1";
    else return "hidden";
  }, [activeIndex, features.length]);

  const getCardStyles = useCallback((position, index) => {
    const isHovered = hoveredIndex === index;
    const baseStyles = {
      transition: "all 0.8s cubic-bezier(0.43, 0.13, 0.23, 0.96)",
      transformOrigin: "center center",
      willChange: "transform, opacity",
    };

    const hoverScale = isHovered ? 1.05 : 1;

    switch (position) {
      case "active":
        return {
          ...baseStyles,
          position: "absolute",
          left: "50%",
          transform: `translateX(-50%) scale(${isHovered ? 1.1 : 1})`,
          width: "min(280px, 90vw)",
          height: "280px",
          zIndex: 30,
          opacity: 1
        };
      case "next1":
        return {
          ...baseStyles,
          position: "absolute",
          left: "50%",
          transform: `translateX(min(100px, 25vw)) scale(${0.85 * hoverScale})`,
          width: "min(260px, 85vw)",
          height: "260px",
          zIndex: 25,
          opacity: 1
        };
      case "next2":
        return {
          ...baseStyles,
          position: "absolute",
          left: "50%",
          transform: `translateX(min(280px, 60vw)) scale(${0.75 * hoverScale})`,
          width: "min(240px, 80vw)",
          height: "240px",
          zIndex: 20,
          opacity: 0.9
        };
      case "next3":
        return {
          ...baseStyles,
          position: "absolute",
          left: "50%",
          transform: `translateX(min(420px, 80vw)) scale(${0.65 * hoverScale})`,
          width: "min(220px, 75vw)",
          height: "220px",
          zIndex: 15,
          opacity: 0.8
        };
      case "prev1":
        return {
          ...baseStyles,
          position: "absolute",
          left: "50%",
          transform: `translateX(max(-350px, -25vw)) scale(${0.85 * hoverScale})`,
          width: "min(260px, 85vw)",
          height: "260px",
          zIndex: 25,
          opacity: 1
        };
      case "prev2":
        return {
          ...baseStyles,
          position: "absolute",
          left: "50%",
          transform: `translateX(max(-500px, -60vw)) scale(${0.75 * hoverScale})`,
          width: "min(240px, 80vw)",
          height: "240px",
          zIndex: 20,
          opacity: 0.9
        };
      case "prev3":
        return {
          ...baseStyles,
          position: "absolute",
          left: "50%",
          transform: `translateX(max(-620px, -80vw)) scale(${0.65 * hoverScale})`,
          width: "min(220px, 75vw)",
          height: "220px",
          zIndex: 15,
          opacity: 0.8
        };
      default:
        return {
          ...baseStyles,
          position: "absolute",
          width: "0px",
          height: "0px",
          opacity: 0,
          zIndex: 0,
          pointerEvents: "none"
        };
    }
  }, [hoveredIndex]);

  return (
    <section
      ref={sectionRef}
      className="w-full bg-white pt-12 md:pt-10 overflow-hidden"
      aria-labelledby="company-section-title"
      role="region"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <header className="text-center mb-12">
          <h2
            id="company-section-title"
            className={`text-3xl md:text-4xl font-bold text-gray-900 mb-4 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
          >
            Why{' '}
            <span className="text-[#F05A28]">RoadExpress</span>
            ?
          </h2>
          <p
            className={`text-lg text-gray-600 max-w-3xl mx-auto transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
            style={{ transitionDelay: '0.2s' }}
          >
            RoadExpress ensures 100% guaranteed quality in every shipment across India. With years of experience in the industry, we are committed to delivering goods safely and reliably, maintaining the highest standards at all times.
          </p>
        </header>

        {/* Cards Container */}
        <div className="relative" role="presentation">
          <div className="w-full h-[400px] flex items-center justify-center">
            <div className="relative w-full h-full flex items-center justify-center">
              {features.map((feature, index) => {
                const position = getCardPosition(index);
                const IconComponent = feature.icon;
                const isActive = position === "active";
                const isVisibleCard = position !== "hidden";

                if (!isVisibleCard) return null;
                return (
                  <article
                    key={`feature-${index}`}
                    className="cursor-pointer overflow-hidden rounded-lg shadow-xl focus:outline-none focus:ring-4 focus:ring-[#F05A28]/50"
                    style={{
                      ...getCardStyles(position, index),
                      backgroundColor: '#F05A28',
                      border: hoveredIndex === index ? '3px solid #E04818' : '2px solid #E04818'
                    }}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleCardClick(index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCardClick(index);
                      }
                    }}
                    tabIndex={isActive ? 0 : -1}
                    role="button"
                    aria-label={feature.ariaLabel}
                    aria-expanded={isActive}
                    aria-describedby={`feature-description-${index}`}
                  >
                    <div className="relative w-full h-full p-4 flex flex-col justify-center">
                      {/* Hover overlay */}
                      <div
                        className="absolute inset-0 bg-black/10 transition-opacity duration-300"
                        style={{
                          opacity: hoveredIndex === index ? 1 : 0
                        }}
                        aria-hidden="true"
                      />

                      {/* Content */}
                      <div className="relative z-10 text-center text-white">
                        {/* Logo/Icon */}
                        <div className="flex justify-center mb-4" aria-hidden="true">
                          <div
                            className={`bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-500 ${isActive ? 'w-16 h-16' : 'w-12 h-12'
                              }`}
                            style={{
                              transform: hoveredIndex === index ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
                              backgroundColor: hoveredIndex === index ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)'
                            }}
                          >
                            <IconComponent
                              className={`text-white ${isActive ? 'w-8 h-8' : 'w-6 h-6'}`}
                              aria-hidden="true"
                            />
                          </div>
                        </div>

                        <h3
                          className={`font-bold text-white mb-3 transition-all duration-300 ${isActive ? "text-xl" : "text-base"
                            }`}
                          style={{
                            transform: hoveredIndex === index ? 'translateY(-2px)' : 'translateY(0)'
                          }}
                        >
                          {feature.title}
                        </h3>

                        <div
                          className="h-1 bg-white/60 mb-3 mx-auto transition-all duration-400"
                          style={{
                            width: isActive ? '40px' : '24px'
                          }}
                          aria-hidden="true"
                        />

                        <p
                          id={`feature-description-${index}`}
                          className={`text-white/90 transition-all duration-300 leading-relaxed ${isActive ? 'text-sm' : 'text-xs'
                            }`}
                          style={{
                            transform: hoveredIndex === index ? 'translateY(-1px)' : 'translateY(0)',
                            opacity: hoveredIndex === index ? 1 : 0.9
                          }}
                        >
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "RoadExpress",
          "description": "Leading logistics company in India with comprehensive transportation solutions",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Logistics Services",
            "itemListElement": features.map((feature, index) => ({
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": feature.title,
                "description": feature.description
              }
            }))
          },
          "serviceType": features.map(f => f.title),
          "areaServed": {
            "@type": "Country",
            "name": "India"
          }
        })}
      </script>

      {/* CSS for performance optimizations */}
      <style jsx>{`
        @media (prefers-reduced-motion: reduce) {
          * {
            transition-duration: 0.01ms !important;
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </section>
  );
};

export default AboutSection;